import {Fragment, useState} from 'react'

import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import KeyHolders from './KeyHolders'
import Modal from '../../shared/Modal'

/**
 * The waiting screen, and the one place the design is limited by what the API reports.
 *
 * A recovery record carries a status and nothing else — no count of the pieces already returned — so
 * every contact here reads `Emailed` and no further. Saying more would mean inventing a tally, and
 * the jump to the last step is the only progress this screen can honestly promise.
 *
 * Nothing polls: contacts reply over hours or days, and an email is sent to no one when the last
 * piece lands, so the user is given the refresh rather than a page that pretends to watch for them.
 */
const AwaitingContacts = ({error = '', ...props}) => {
  const [confirming, setConfirming] = useState(false)
  const {holders} = props

  const toggleConfirming = () => {
    setConfirming(!confirming)
  }

  const askAgain = () => {
    setConfirming(false)
    props.onAsk()
  }

  return (
    <Fragment>
      <Paper elevation={3} component='div' className='recovery-step'>
        <Typography variant='h4' gutterBottom>Waiting on your contacts</Typography>
        <Typography variant='body1' gutterBottom>
          We've emailed everyone holding a piece of your key. As soon as three of them send it back,
          you'll be able to finish here.
        </Typography>
        <KeyHolders holders={holders} chip={{label: 'Emailed', color: 'warning'}} />
        <Typography className='caption' variant='body2' gutterBottom>
          We can't see who has replied yet. Come back to this page and we'll show you the last step
          as soon as three pieces are in.
        </Typography>
        {error.length > 0 && <Typography variant='body1' color='error' gutterBottom>{error}</Typography>}
        <ButtonGroup direction='row'>
          <Button disabled={props.loading} onClick={props.onRefresh}>Check now</Button>
          <Button disabled={props.loading} onClick={toggleConfirming}>Send the emails again</Button>
        </ButtonGroup>
      </Paper>
      <Modal
        maxWidth='sm'
        open={confirming}
        handleClose={toggleConfirming}
        buttonText='Close'
        title='Send your contacts a new email?'>
        <Typography gutterBottom>
          The links we already sent will stop working straight away. Anyone part-way through the old
          email will have to start again with the new one.
        </Typography>
        <ButtonGroup direction='row'>
          <Button onClick={toggleConfirming}>Keep the old ones</Button>
          <Button color='primary' loading={props.loading} onClick={askAgain}>Send new emails</Button>
        </ButtonGroup>
      </Modal>
    </Fragment>
  )
}

AwaitingContacts.propTypes = {
  holders: PropTypes.array.isRequired,
  onAsk: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool
}

export default AwaitingContacts
